import React, { Component } from "react";
// import { Popover, Skeleton } from "antd";
import { Popover } from "antd";
import LoadingAnimation from "../../components/common/LoadingAnimation";
import ReactFullscreen from "react-easyfullscreen";
import ReportWidjets from "./ReportWidjets";
import DownloadMenu from "../../components/common/DownloadMenu";
// the editor toolbar's Refresh icon - same asset, so reload looks the same in both places
import RefreshIcon from "../../assets/icons/reset-icon.svg";
import EmailShareIcon from "../../assets/icons/email-share.svg";
import "./dashboardStyles.css";

/**
 * A board as readers see it: the header (title, row count, date), the board toolbar
 * (auto refresh, download, email, refresh, full screen) and the widgets with their
 * filters. Lifted out of the board view page (views/Dashboard) unchanged, so the
 * editor's Preview can draw exactly the same thing — same component, same styles,
 * same one-widget-takes-the-page rule — instead of a look-alike that drifts.
 *
 * What differs between the two callers comes in as props:
 *   board                 the board to draw (the view page's saved one, or Preview's
 *                         copy built from the editor's unsaved state)
 *   selectedResource, onApplyGlobalFilter, onApplyDataFilter, onResetDataFilter
 *                         filter handling, exactly as ReportWidjets takes it
 *   headerActions         buttons after the toolbar (the view page's Back/Edit/Delete)
 *   onEmail               shows the Email button; omitted, there is none (Preview)
 *   reloadsInPlace        Refresh / auto refresh refetch the widgets instead of
 *                         reloading the page — Preview must not throw away the editor
 *   isLoading             the board itself is still being fetched (view page)
 *   loadError, onRetry    the board could not be fetched: the message, and how to try
 *                         again (view page)
 *
 * The widgets' instance is reachable as `widgetsRef` for callers that need it (the
 * view page's Email builds its attachments through it).
 */
export default class BoardSurface extends Component {
  constructor(props) {
    super(props);
    this.state = {
      anchorEl: null,
      /* Whether the widgets are still fetching. Starts true so Download and Email are
         disabled on first paint rather than enabling for the instant before the widgets
         report in. ReportWidjets owns the truth - see its reportBusyState. */
      isWidgetsBusy: true,
      /* Rows in the lone widget's table, for the pill beside the page title. Undefined
         until a widget reports one, which is what keeps the pill off a board that is
         still loading and off one whose single widget is a chart. */
      soleRowCount: undefined,
      /* Whether the board is currently filling the screen. Read off the document (see
         syncFullScreen) rather than set by the button, because Esc and F11 change it
         too - the header has to keep saying which way the toggle will go. */
      isFullScreen: false,
    };
    // the widget rows live inside ReportWidjets, so board-level actions go through here
    this.widgetsRef = React.createRef();
  }

  syncFullScreen = () => {
    const current = document.fullscreenElement || document.webkitFullscreenElement;
    this.setState({ isFullScreen: Boolean(current) });
  };

  componentDidMount() {
    document.addEventListener("fullscreenchange", this.syncFullScreen);
    document.addEventListener("webkitfullscreenchange", this.syncFullScreen);
  }

  componentWillUnmount() {
    document.removeEventListener("fullscreenchange", this.syncFullScreen);
    document.removeEventListener("webkitfullscreenchange", this.syncFullScreen);
    clearInterval(this.autoRefreshTimer);
  }

  handleVisibleChange = (visible) => this.setState({ anchorEl: visible });

  /* In Preview the board sits in an antd Drawer. Menus and popovers mount on the page
     body by default - outside the drawer - and a click on them closed it. There they
     mount inside the drawer instead; on the view page nothing changes (undefined =
     antd's default). */
  popupContainer = () =>
    this.props.reloadsInPlace
      ? (trigger) => (trigger && trigger.closest(".ant-drawer-body")) || document.body
      : undefined;

  // Reload: the page on the view page, the widgets alone in Preview (see reloadsInPlace).
  reload = () => {
    if (this.props.reloadsInPlace) this.widgetsRef.current?.fetchReport?.();
    else window.location.reload(false);
  };

  handleInterval = (_event) => {
    const seconds = Number(_event.target.dataset.value);
    if (this.props.reloadsInPlace) {
      // Preview: refetch on the chosen interval, stopped when Preview closes.
      clearInterval(this.autoRefreshTimer);
      if (seconds < 1000000) this.autoRefreshTimer = setInterval(this.reload, seconds * 1000);
    } else if (localStorage.getItem("setTimeRefresh")) {
      setInterval(() => window.location.reload(false), Number(localStorage.getItem("setTimeRefresh")));
    } else {
      setInterval(() => window.location.reload(false), seconds * 1000);
    }
    this.setState({ anchorEl: false });
  };

  render() {
    // const { board, isLoading, headerActions, onEmail } = this.props;
    const { board, isLoading, headerActions, onEmail, loadError, onRetry } = this.props;
    const isReport = board && board.configDetails && board.configDetails.boardType === "reports";
    const typeLabel = isReport ? "Report" : "Dashboard";
    const boardProps = (board && board.properties) || {};

    /* ── A ONE-WIDGET BOARD'S BUTTONS ──
       A board holding a single widget draws that widget with no header bar of its own
       (see ReportWidjets.isSingleWidgetBoard), which is where its Download and Full
       screen buttons used to live. They are not dropped: the widget IS the page here,
       so the board toolbar answers for it, the way it always has on a report. A board
       with several widgets is untouched - each widget keeps its own header and buttons. */
    const resources = (board && board.resources) || [];
    const isSingleWidget = isReport || resources.length === 1;
    const soleWidgetProps = isSingleWidget ? resources[0]?.properties || {} : {};

    /* ── WHERE THE DOWNLOAD MENU GOES ──
       Here, and only on a one-widget board. That widget draws no header of its own, so
       the page header is the only place left to put its menu. A board with several
       widgets gets nothing here: each widget carries its own menu in its own header.

       PDF, PNG and XLS are always in the menu; only CSV waits on the editor's toggle -
       widget-level on a dashboard, board-level on a report, so either switch opens it. */
    const showDownload = isSingleWidget;
    const allowCsv = Boolean(soleWidgetProps.csvdownload || boardProps.csvdownload);
    const showFullScreen = Boolean(boardProps.fullscreen || soleWidgetProps.fullscreen);
    // Email is the caller's to offer - the view page does, Preview does not.
    const showEmail = Boolean(onEmail);

    /* ── THE WHOLE TOOLBAR IS DISABLED WHILE THE WIDGETS LOAD ──
       Every button here acts on a board that is not there yet. Reported up from
       ReportWidjets - see its reportBusyState. */
    const isBusy = this.state.isWidgetsBusy;

    const hasToolbar = boardProps.autorefresh || showFullScreen || showDownload || showEmail || boardProps.refresh;
    const today = new Date().toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    /* ── TOOLBAR ICONS ──
       Inline SVG rather than <MaterialDesignIcon>, which draws a ligature: the word
       ("fullscreen") the remote icon font is supposed to replace - shown as text
       whenever the font does not arrive. */
    const AutoRefreshIcon = (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
        <path d="M8 4.5V8l2.2 1.3" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M13.8 8A5.8 5.8 0 1 1 11.9 3.7" strokeLinecap="round" />
        <path d="M13.9 1.9v2.9h-2.9" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    const EmailIcon = <img src={EmailShareIcon} alt="" className="dbv-tool-icon" />;
    const FullScreenIcon = (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
        <path d="M6 2.5H2.5V6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
    // arrows turned inward - the way back out of full screen
    const FullScreenExitIcon = (
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
        <path
          d="M2.5 6H6V2.5M13.5 6H10V2.5M10 13.5V10h3.5M6 13.5V10H2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    return (
      <ReactFullscreen>
        {({ ref, onRequest, onExit }) => (
          <div ref={ref} className={`view dbv-surface${this.state.isFullScreen ? " dbv-surface--fullscreen" : ""}`}>
            {/* ── Header ── */}
            <div className="dbv-header">
              <div className="dbv-header-left">
                <div className="dbv-title-row">
                  <h1 className="dbv-title">{board ? board.name : typeLabel}</h1>
                  {/* How many rows the table holds, after its filters - one-widget boards
                      only; with several widgets each carries its own count. */}
                  {Number.isFinite(this.state.soleRowCount) ? (
                    <span className="dbv-title-count">{this.state.soleRowCount.toLocaleString()} total</span>
                  ) : null}
                </div>
                <p className="dbv-subtitle">{today}</p>
              </div>

              <div className="dbv-header-actions">
                {hasToolbar ? (
                  <div className="dbv-tools">
                    {boardProps.autorefresh ? (
                      <Popover
                        content={
                          <div className="dbv-interval-list">
                            <a data-value="1000000" onClick={this.handleInterval}>
                              Never
                            </a>
                            <a data-value="30" onClick={this.handleInterval}>
                              30 Seconds
                            </a>
                            <a data-value="60" onClick={this.handleInterval}>
                              1 Minute
                            </a>
                            <a data-value="900" onClick={this.handleInterval}>
                              15 Minutes
                            </a>
                            <a data-value="3600" onClick={this.handleInterval}>
                              1 Hour
                            </a>
                          </div>
                        }
                        title="Refresh Interval"
                        trigger="click"
                        visible={this.state.anchorEl}
                        onVisibleChange={this.handleVisibleChange}
                        placement="bottomRight"
                        getPopupContainer={this.popupContainer()}
                      >
                        <button
                          className="dbv-tool-btn"
                          disabled={isBusy}
                          title={isBusy ? "Loading…" : "Auto refresh interval"}
                        >
                          {AutoRefreshIcon}
                        </button>
                      </Popover>
                    ) : null}
                    {showDownload ? (
                      <DownloadMenu
                        className="dbv-tool-btn"
                        allowCsv={allowCsv}
                        disabled={isBusy}
                        onSelect={(format) => this.widgetsRef.current?.exportSoleWidget?.(format)}
                        title={isBusy ? "Loading…" : `Download this ${typeLabel.toLowerCase()}`}
                        getPopupContainer={this.popupContainer()}
                      />
                    ) : null}
                    {showEmail ? (
                      <button
                        className="dbv-tool-btn"
                        disabled={isBusy}
                        title={isBusy ? "Loading…" : `Email this ${typeLabel.toLowerCase()}`}
                        onClick={onEmail}
                      >
                        {EmailIcon}
                      </button>
                    ) : null}
                    {boardProps.refresh ? (
                      <button
                        className="dbv-tool-btn"
                        disabled={isBusy}
                        title={isBusy ? "Loading…" : this.props.reloadsInPlace ? "Reload data" : "Reload page"}
                        onClick={this.reload}
                      >
                        <img src={RefreshIcon} alt="" className="dbv-tool-icon" />
                      </button>
                    ) : null}
                    {showFullScreen ? (
                      <button
                        className={`dbv-tool-btn${this.state.isFullScreen ? " dbv-tool-btn--active" : ""}`}
                        disabled={isBusy}
                        title={isBusy ? "Loading…" : this.state.isFullScreen ? "Exit full screen" : "Full screen"}
                        onClick={() => (this.state.isFullScreen ? onExit() : onRequest())}
                      >
                        {this.state.isFullScreen ? FullScreenExitIcon : FullScreenIcon}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {headerActions}
              </div>
            </div>

            {/* ── Widgets ── */}
            {/* {isLoading ? (
              <div className="dbv-skeleton">
                <Skeleton active />
              </div>
            ) : board ? ( */}
            {/* While the board itself loads: the same spinner the widgets show while
                THEIR data loads (ReportWidjets' report-widget-loader), not grey skeleton
                bars - so the page shows one loading state from start to finish. */}
            {isLoading ? (
              <div className="report-widget report-widget--loading">
                <div className="report-widget-loader">
                  <div className="report-widget-loader-inner">
                    <LoadingAnimation size="large" text="Loading…" />
                  </div>
                </div>
              </div>
            ) : board ? (
              <div id="report-div" className="dbv-widgets">
                <ReportWidjets
                  ref={this.widgetsRef}
                  board={board}
                  selectedResource={this.props.selectedResource}
                  onApplyGlobalFilter={this.props.onApplyGlobalFilter}
                  onApplyDataFilter={this.props.onApplyDataFilter}
                  onResetDataFilter={this.props.onResetDataFilter}
                  // keeps Download and Email disabled until there are rows to act on
                  onBusyChange={(busy) => this.setState({ isWidgetsBusy: busy })}
                  // feeds the "N total" pill beside the title on a one-widget board
                  onRowCountChange={(count) => this.setState({ soleRowCount: count })}
                />
              </div>
            ) : loadError ? (
              /* The board request failed (a 500, a deleted board). This used to fall
                 through to nothing - an empty page, indistinguishable from a board
                 with no widgets. */
              <div className="dbv-load-error" role="alert">
                <span>{loadError}</span>
                {onRetry ? (
                  <button type="button" onClick={onRetry}>
                    Retry
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </ReactFullscreen>
    );
  }
}
